import cx from 'clsx';
import Head from 'next/head';
import { useRouter } from 'next/router';
import PropTypes from 'prop-types';
import { useCallback, useEffect, useMemo, useState } from 'react';

import Button from '@placekit/uikit/components/Button';
import Icon from '@placekit/uikit/components/Icon';
import Link from '@placekit/uikit/components/Link';
import Logo from '@placekit/uikit/components/Logo';
import { useScrollReveal } from '@placekit/uikit/features/useScrollReveal';
import { useClickOutside } from '@placekit/uikit/features/useClickOutside';

import Container from 'components/Container';
import Footer from 'components/Footer';
import Transition from 'components/Transition';
import { useBrowserLanguage } from 'features/useBrowserLanguage';
import { useLocale, useT } from 'i18n';
import { graph, organization, website } from 'utils/jsonld';
import { absolute } from 'utils/url';

const HINT_DISMISSED_KEY = 'placekit:language-hint-dismissed';

/**
 * EN | FR switcher, rendered only for pages that have a twin in both languages.
 */
const LanguageSwitcher = ({ alternates, locale, className }) => {
  const t = useT();
  const targets = alternates.filter((alt) => alt.hrefLang !== 'x-default');
  if (targets.length < 2) {
    return null;
  }
  return (
    <nav
      aria-label={t('common.language.label')}
      className={cx(
        'flex items-center divide-x divide-gray-300/60 rounded-md border border-gray-300/60 overflow-hidden text-sm leading-none',
        className,
      )}
    >
      {targets.map(({ hrefLang, path }) => (
        <Link
          key={hrefLang}
          href={path}
          hrefLang={hrefLang}
          lang={hrefLang}
          aria-current={hrefLang === locale ? 'true' : undefined}
          title={t('common.language.switchTo', {
            language: t(`common.language.${hrefLang}Full`),
          })}
          className={cx('px-2 py-1.5', {
            'bg-accent-50 font-semibold text-accent-600': hrefLang === locale,
            'text-gray-500 hover:text-accent-600 focus:text-accent-600': hrefLang !== locale,
          })}
        >
          {t(`common.language.${hrefLang}`)}
        </Link>
      ))}
    </nav>
  );
};

LanguageSwitcher.propTypes = {
  alternates: PropTypes.arrayOf(
    PropTypes.shape({ hrefLang: PropTypes.string, path: PropTypes.string }),
  ).isRequired,
  locale: PropTypes.string.isRequired,
  className: PropTypes.string,
};

/**
 * Non-intrusive hint shown when the visitor's browser language matches the other
 * available translation of the current page. Never redirects, and the dismissal
 * is remembered for the visitor.
 * The hint strings are deliberately written in the *other* language.
 */
const LanguageHint = ({ alternates, locale }) => {
  const t = useT();
  const browserLanguage = useBrowserLanguage(locale);
  const [dismissed, setDismissed] = useState(false);
  const target = alternates.find(
    (alt) => alt.hrefLang !== 'x-default' && alt.hrefLang === browserLanguage,
  );

  // Read after hydration: localStorage is per-visitor and can throw (private
  // mode, blocked site data), and reading it during render would desync SSR.
  useEffect(() => {
    try {
      if (window.localStorage.getItem(HINT_DISMISSED_KEY)) {
        setDismissed(true);
      }
    } catch {
      // storage unavailable: the hint simply reappears next time
    }
  }, []);

  const dismiss = useCallback(() => {
    setDismissed(true);
    try {
      window.localStorage.setItem(HINT_DISMISSED_KEY, '1');
    } catch {
      // storage unavailable: nothing to persist
    }
  }, []);

  if (dismissed || !target || target.hrefLang === locale) {
    return null;
  }

  return (
    <div className="border-b border-gray-200 bg-accent-50" lang={target.hrefLang}>
      <Container className="flex items-center justify-center gap-3 py-2 text-sm text-gray-700">
        <Icon name="language" size="small" className="flex-none text-accent-600" />
        <p>
          {t('common.language.crossHint')}{' '}
          <Link
            href={target.path}
            hrefLang={target.hrefLang}
            className="font-semibold text-accent-600 underline underline-offset-2 hover:text-accent-700 focus:text-accent-700"
          >
            {t('common.language.crossHintCta')}
          </Link>
        </p>
        <Button
          label={t('common.language.crossHintDismiss')}
          onClick={dismiss}
          iconOnly={true}
          icon="x-mark"
          kind="none"
          className="flex-none text-gray-500 hover:text-gray-900 focus:text-gray-900"
        />
      </Container>
    </div>
  );
};

LanguageHint.propTypes = {
  alternates: PropTypes.arrayOf(
    PropTypes.shape({ hrefLang: PropTypes.string, path: PropTypes.string }),
  ).isRequired,
  locale: PropTypes.string.isRequired,
};

const MainShell = ({ meta = {}, nav = 'full', children }) => {
  const [navOpen, setNavOpen] = useState(false);
  const clickOutsideRef = useClickOutside(() => setNavOpen(false));
  const locale = useLocale();
  const t = useT();

  // close nav when route changes (link is clicked)
  const router = useRouter();

  useEffect(() => {
    const onRouteChange = () => {
      setNavOpen(false);
    };
    router.events.on('routeChangeStart', onRouteChange);
    router.events.on('hashChangeStart', onRouteChange);
    return () => {
      router.events.off('routeChangeStart', onRouteChange);
      router.events.off('hashChangeStart', onRouteChange);
    };
  }, [router]);

  // set scroll reveal feature for header
  const scrollReveal = useScrollReveal({
    showDelay: 100,
    hideDelay: 300,
    threshold: 80,
  });

  const canonical = useMemo(() => absolute(router.asPath.split(/[?#]/)[0]), [router.asPath]);

  const navLinks = useMemo(
    () => [
      { label: t('common.nav.features'), href: t.path('/#features'), scroll: false },
      { label: t('common.nav.pricing'), href: t.path('/pricing') },
      { label: t('common.nav.developers'), href: '/developers' },
      { label: t('common.nav.blog'), href: '/blog' },
    ],
    [t],
  );

  const mobileLinks = useMemo(
    () => [
      ...navLinks,
      { label: t('common.nav.terms'), href: '/terms' },
      { label: t('common.nav.about'), href: '/about' },
      { label: t('common.nav.contact'), href: '/about#contact', scroll: false },
      { label: t('common.nav.status'), href: 'https://placekit.statuspage.io', target: '_blank' },
    ],
    [navLinks, t],
  );

  const alternates = useMemo(() => meta.alternates || [], [meta.alternates]);

  const title = [meta.title, 'PlaceKit'].filter((s) => !!s).join(' | ');
  const image = meta.image || `${process.env.NEXT_PUBLIC_BASE_URL}/placekit-opengraph.png`;
  const jsonLd = useMemo(
    () => graph(organization(), website(), ...(meta.jsonLd || [])),
    [meta.jsonLd],
  );

  return (
    <>
      <Head>
        <title>{title}</title>
        {/* Known, pre-existing quirk: next/head does not swap this <link> on a
            client-side navigation (og:url and the hreflang links do update), so
            the in-page canonical goes stale until the next full load. Harmless
            for crawlers, which fetch every URL fresh and get the prerendered
            head. Tracked separately, not an i18n regression. */}
        <link key="canonical" rel="canonical" href={canonical} />
        {alternates.map(({ hrefLang, path }) => (
          <link
            key={`alternate-${hrefLang}`}
            rel="alternate"
            hrefLang={hrefLang}
            href={absolute(path)}
          />
        ))}
        {!!meta.description && <meta name="description" content={meta.description} />}
        <meta property="og:site_name" content="PlaceKit" />
        <meta property="og:locale" content={meta.locale || 'en_US'} />
        <meta property="og:url" content={canonical} />
        <meta property="og:title" content={title} />
        {!!meta.description && <meta property="og:description" content={meta.description} />}
        <meta property="og:type" content={meta.type || 'website'} />
        <meta property="og:image" content={image} />
        <meta property="og:image:alt" content={meta.imageAlt || 'PlaceKit logo'} />
        {meta.type === 'article' && !!meta.publishedTime && (
          <meta property="article:published_time" content={meta.publishedTime} />
        )}
        {meta.type === 'article' && !!meta.modifiedTime && (
          <meta property="article:modified_time" content={meta.modifiedTime} />
        )}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:site" content="@placekitapi" />
        <meta name="twitter:title" content={title} />
        {!!meta.description && <meta name="twitter:description" content={meta.description} />}
        <meta name="twitter:image" content={image} />
        <meta name="twitter:image:alt" content={meta.imageAlt || 'PlaceKit logo'} />
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLd).replace(/</g, '\\u003c') }}
        />
      </Head>

      <header
        ref={clickOutsideRef}
        className={cx(
          'fixed top-0 inset-x-0 z-30 max-h-screen overflow-y-auto',
          'bg-gray-50/80',
          'transition-all ease-out duration-300 transform-gpu',
          {
            'backdrop-blur-lg border-b border-gray-300/40': !scrollReveal.isAtOrigin || navOpen,
            '-translate-y-full md:translate-y-0': scrollReveal.isHidden && !navOpen,
          },
        )}
      >
        <Container
          className={cx(
            'flex items-center justify-between h-20 gap-x-2',
            'transition-all ease-out duration-300 transform-gpu',
            {
              'md:h-40': scrollReveal.isAtOrigin,
            },
          )}
        >
          <Link
            href={t.path('/')}
            className="flex items-center space-x-0.5 sm:space-x-2 relative sm:mr-6 xl:mr-16 text-accent-500"
            aria-label={t('common.header.homeAria')}
          >
            <Logo full={true} size="medium" className="sm:h-8" />
          </Link>
          {nav === 'full' && (
            <nav className="grow hidden md:flex gap-x-6 xl:gap-x-10">
              {navLinks.map(({ label, ...linkProps }, i) => (
                <Link
                  key={i.toString()}
                  {...linkProps}
                  className="text-gray-500 hover:text-accent-600 focus:text-accent-600 hover:underline focus:underline underline-offset-2 decoration-2 decoration-accent-100"
                >
                  {label}
                </Link>
              ))}
            </nav>
          )}
          <div className="flex items-center gap-x-2 sm:gap-x-4">
            {nav === 'full' && (
              <>
                <LanguageSwitcher
                  alternates={alternates}
                  locale={locale}
                  className="max-md:hidden"
                />
                <Button
                  href="https://app.placekit.io/auth/signin"
                  label={t('common.header.signIn')}
                  kind="none"
                  className="max-md:hidden md:inline-block text-gray-500 hover:bg-accent-50 hover:text-accent-600 focus:bg-accent-50 focus:text-accent-600"
                />
              </>
            )}
            <Button
              href="https://app.placekit.io/auth/register"
              label={t('common.header.getStarted')}
              kind="primary"
            />
            {nav === 'full' && (
              <Button
                label={t('common.header.toggleNav')}
                onClick={() => setNavOpen((prev) => !prev)}
                iconOnly={true}
                icon={navOpen ? 'x-mark' : 'bars-3'}
                kind="none"
                className="relative md:hidden z-50 text-gray-500 focus:text-gray-900 active:ring-0 focus:ring-0 active:ring-offset-0 focus:ring-offset-0"
              />
            )}
          </div>
        </Container>
        {nav === 'full' && (
          <Transition
            show={navOpen}
            enter="transition ease-in-out duration-150"
            enterFrom="opacity-0 -translate-y-4"
            enterTo="opacity-100 translate-y-0"
            leave="transition duration-150"
            leaveFrom="opacity-100 translate-y-0"
            leaveTo="opacity-0 -translate-y-4"
            className={cx('flex-col gap-y-4 p-4 sm:px-6 border-t border-gray-300/40', {
              hidden: !navOpen,
              'flex md:hidden': navOpen,
            })}
          >
            {mobileLinks.map(({ label, ...linkProps }, i) => (
              <Link
                key={i.toString()}
                {...linkProps}
                className="flex items-center justify-between text-gray-600 hover:text-accent-600 focus:text-accent-600"
              >
                <span>{label}</span>
                {linkProps.target === '_blank' && (
                  <Icon name="arrow-top-right-on-square" size="small" className="opacity-80" />
                )}
              </Link>
            ))}
            <hr className="border-gray-300/20" />
            <Link
              href="https://app.placekit.io"
              className="text-gray-600 hover:text-accent-600 focus:text-accent-600"
            >
              {t('common.header.signIn')}
            </Link>
            <LanguageSwitcher alternates={alternates} locale={locale} className="self-start" />
          </Transition>
        )}
      </header>

      <div className="w-full min-h-screen flex flex-col pt-20 md:pt-40">
        {alternates.length > 1 && <LanguageHint alternates={alternates} locale={locale} />}
        <main className="relative grow items-center">{children}</main>
        <Footer />
      </div>
    </>
  );
};

MainShell.propTypes = {
  /** Page meta */
  meta: PropTypes.shape({
    title: PropTypes.string,
    description: PropTypes.string,
    type: PropTypes.string,
    image: PropTypes.string,
    imageAlt: PropTypes.string,
    locale: PropTypes.string,
    publishedTime: PropTypes.string,
    modifiedTime: PropTypes.string,
    /** hreflang alternates, for pages translated in several languages */
    alternates: PropTypes.arrayOf(
      PropTypes.shape({
        hrefLang: PropTypes.string.isRequired,
        path: PropTypes.string.isRequired,
      }),
    ),
    /** Extra schema.org nodes appended to the page JSON-LD graph */
    jsonLd: PropTypes.arrayOf(PropTypes.object),
  }),
  /** Navigation style */
  nav: PropTypes.oneOf(['full', 'compact']),
  /** Content */
  children: PropTypes.node,
};

export default MainShell;
